import { Controller } from "@hotwired/stimulus"

/**
 * Agent Run session-detail page: the small colored-segment scrubber above
 * the transcript, AND the transcript's row-click → right-panel behavior.
 * Both live in one controller instance/scope (shared root, see show.html.erb)
 * because they cooperate on the same transcript DOM and there's no benefit
 * to splitting them into two controllers that would just have to agree on
 * the same data-entity-id lookups.
 *
 * jumpTo(): clicking a scrubber segment scrolls the matching turn into view
 * and briefly highlights it — no panel to keep in sync, just a scroll +
 * flash, so there's nothing here that can drift out of sync with the
 * server-rendered transcript.
 *
 * Finds the turn by data-entity-id directly (a plain attribute on each
 * turn's wrapping <div>, set by Ui::LogRowComponent) rather than a Stimulus
 * target — there's no need for one when a single attribute lookup does
 * the job, and it keeps this controller decoupled from the transcript's
 * markup structure.
 *
 * selectRow(): clicking a Ui::LogRowComponent row clones that row's own
 * `[data-role="detail"]` content into the right-hand panel for the SAME
 * tab (each tab panel has its own `panel` target, scoped via the nearest
 * ancestor `[role="tabpanel"]` so clicking a row in "Main" never touches a
 * sub-agent tab's panel, and vice versa) and marks the row selected via
 * `data-selected` (Ui::LogRowComponent's root already styles that
 * attribute). Rows with no `with_detail` block (detail? false server-side,
 * so no `[data-role="detail"]` node at all) are not clickable targets for
 * this — selectRow no-ops for them.
 */
export default class extends Controller {
  jumpTo(event) {
    const stepId = event.currentTarget?.dataset.stepId
    if (!stepId) return

    const turn = this.element.querySelector(`[data-entity-id="${stepId}"]`)
    if (!turn) return

    turn.scrollIntoView({ behavior: "smooth", block: "center" })
    turn.classList.add("ring-2", "ring-primary", "rounded-lg")
    setTimeout(
      () => turn.classList.remove("ring-2", "ring-primary", "rounded-lg"),
      1200,
    )
  }

  selectRow(event) {
    const row = event.currentTarget
    const detail = row.querySelector(':scope > [data-role="detail"]')
    if (!detail) return

    const tabPanel = row.closest('[role="tabpanel"]') || this.element
    const panel = tabPanel.querySelector(
      '[data-ai--agent-run-scrubber-target="panel"]',
    )
    if (!panel) return

    for (const sibling of tabPanel.querySelectorAll("[data-entity-id]")) {
      sibling.dataset.selected = sibling === row ? "true" : "false"
    }

    // Clone the detail node's children, not the node itself — the node
    // carries `class="hidden"` so it stays invisible inline in the
    // transcript; cloning it wholesale would carry that class into the
    // panel and leave the content invisible there too.
    const clone = detail.cloneNode(true)
    panel.replaceChildren(...clone.childNodes)
  }
}
